import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, Calendar, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import StripePaymentForm from './StripePaymentForm';
import ModalShell from '../shared/ModalShell';
import {
  resolvePaymentTerms,
  calculateRemainingPayments,
  rulesForPaymentCalculation,
  bookingPaymentScheduleRules,
} from '../../utils/paymentTerms';

const BalancePaymentModal = ({
  isOpen,
  onClose,
  booking,
  balanceAmount,
  payments = [],
  onPaymentSuccess,
  onPaymentError,
}) => {
  const [cardholderName, setCardholderName] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentSchedule, setShowPaymentSchedule] = useState(true);
  const [payAllRemaining, setPayAllRemaining] = useState(true);

  const paymentTerms = useMemo(() => {
    if (!booking?.event_date) return null;
    const subtotal = parseFloat(booking?.subtotal_amount || 0);
    const platformFee = parseFloat(booking?.platform_fee || 0);
    const tax = parseFloat(booking?.tax_amount || 0);
    const rules = rulesForPaymentCalculation(bookingPaymentScheduleRules(booking));
    return resolvePaymentTerms(booking.event_date, subtotal, rules, {
      platformFee,
      tax,
      bookingDate: booking.created_at || new Date(),
    });
  }, [
    booking?.event_date,
    booking?.created_at,
    booking?.subtotal_amount,
    booking?.platform_fee,
    booking?.tax_amount,
    booking?.payment_schedule_rules,
    booking?.invoice?.payment_schedule_rules,
    booking?.vendor_service_package?.payment_schedule_rules,
    booking?.package?.payment_schedule_rules,
  ]);

  const completedPayments = useMemo(() => {
    return payments.filter((p) => p.status === 'completed') || [];
  }, [payments]);

  const hasPriorPayments = completedPayments.length > 0;

  // Sequential plan: only the next unpaid installment (plus "Pay all remaining" when it differs).
  const remainingPayments = useMemo(() => {
    if (!paymentTerms || !booking) return [];
    const subtotal = parseFloat(booking.subtotal_amount || 0);
    const platformFee = parseFloat(booking.platform_fee || 0);
    const allRemaining = calculateRemainingPayments(
      paymentTerms,
      completedPayments,
      subtotal,
      platformFee
    ).filter((payment) => (payment.remainingAmount || 0) > 0.01);

    if (allRemaining.length === 0) return [];
    return [allRemaining[0]];
  }, [paymentTerms, completedPayments, booking]);

  // Final (or only) installment equals the balance → show one option, not both.
  const isFinalInstallmentOnly = useMemo(() => {
    if (remainingPayments.length === 0) return true;
    const next = remainingPayments[0];
    const nextAmt = Number(next.remainingAmount ?? next.amount ?? 0);
    return Math.abs(nextAmt - Number(balanceAmount || 0)) < 0.02;
  }, [remainingPayments, balanceAmount]);

  const showPayAllOption = !isFinalInstallmentOnly && Number(balanceAmount || 0) > 0.01;

  const didInitRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      didInitRef.current = false;
      return;
    }
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (remainingPayments.length === 0 || isFinalInstallmentOnly) {
      // Single remaining amount: pay balance (no duplicate installment row).
      setPayAllRemaining(true);
      setSelectedPayment(null);
      return;
    }

    // Default to the next installment in sequence (not later ones).
    setPayAllRemaining(false);
    setSelectedPayment(remainingPayments[0]);
  }, [isOpen, remainingPayments, isFinalInstallmentOnly]);

  const selectedPaymentAmount = useMemo(() => {
    if (payAllRemaining) return balanceAmount;
    if (!selectedPayment || !booking) return balanceAmount;
    return selectedPayment.remainingAmount || selectedPayment.amount;
  }, [selectedPayment, booking, balanceAmount, payAllRemaining]);

  const paymentType = useMemo(() => {
    if (payAllRemaining) return 'full_payment';
    if (!selectedPayment) return 'full_payment';
    const isDeposit =
      !hasPriorPayments &&
      (selectedPayment.isBookingDeposit ||
        selectedPayment.index === 0 ||
        /deposit/i.test(selectedPayment.label || ''));
    return isDeposit ? 'deposit' : 'full_payment';
  }, [payAllRemaining, selectedPayment, hasPriorPayments]);

  const paymentMetadata = useMemo(
    () => ({
      cardholderName,
      onCardholderNameChange: setCardholderName,
      paymentLabel: payAllRemaining
        ? 'Pay all remaining balance'
        : selectedPayment
          ? `${selectedPayment.label || 'Installment'}${
              selectedPayment.percentage != null
                ? ` (${selectedPayment.percentage}%)`
                : ''
            }`
          : 'Balance payment',
      paymentPercentage: payAllRemaining
        ? (() => {
            const totalDue =
              parseFloat(booking?.subtotal_amount || 0) +
              parseFloat(booking?.platform_fee || 0);
            return totalDue > 0 ? (balanceAmount / totalDue) * 100 : null;
          })()
        : selectedPayment?.percentage || null,
      paymentPlatformFee: payAllRemaining
        ? (() => {
            const totalDue =
              parseFloat(booking?.subtotal_amount || 0) +
              parseFloat(booking?.platform_fee || 0);
            const remainingPercentage =
              totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
            return (
              (parseFloat(booking?.platform_fee || 0) * remainingPercentage) / 100
            );
          })()
        : selectedPayment
          ? (parseFloat(booking?.platform_fee || 0) * selectedPayment.percentage) /
            100
          : null,
      totalPlatformFee: parseFloat(booking?.platform_fee || 0),
    }),
    [
      cardholderName,
      payAllRemaining,
      selectedPayment,
      balanceAmount,
      booking?.subtotal_amount,
      booking?.platform_fee,
    ]
  );

  const open = Boolean(isOpen && booking && balanceAmount > 0);
  const subtotal = parseFloat(booking?.subtotal_amount || 0);
  const platformFee = parseFloat(booking?.platform_fee || 0);
  const amountPaid = parseFloat(booking?.amount_paid || 0);

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      labelledBy="balance-payment-title"
      className="items-end justify-center p-0 sm:items-center sm:p-4 sm:p-6"
      panelClassName="flex max-h-[min(100dvh,100vh)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-xl sm:max-h-[min(92dvh,900px)] sm:rounded-xl"
    >
      {booking && balanceAmount > 0 && (
        <>
          {/* Sticky header */}
          <div className="sticky top-0 z-20 shrink-0 border-b border-border bg-surface px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 sm:pt-4">
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-border sm:hidden" aria-hidden />
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 pr-2">
                <h2
                  id="balance-payment-title"
                  className="text-base font-bold text-ink sm:text-xl"
                >
                  {hasPriorPayments ? 'Pay remaining balance' : 'Make a payment'}
                </h2>
                <p className="mt-0.5 text-xs text-ink-secondary sm:text-sm break-words">
                  {hasPriorPayments
                    ? `Booking #${booking.booking_reference}`
                    : `Plan for booking #${booking.booking_reference}`}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-md p-2 text-ink-tertiary transition-colors hover:bg-surface-muted hover:text-ink"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-5 sm:py-4 [-webkit-overflow-scrolling:touch]">
            <div className="mb-3 rounded-xl border border-border bg-surface-muted p-3 sm:mb-4 sm:p-4">
              <div className="space-y-1.5 text-xs sm:text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-ink-secondary">Subtotal</span>
                  <span className="font-medium tabular-nums text-ink">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-ink-secondary">Service charge</span>
                  <span className="font-medium tabular-nums text-ink">
                    ${platformFee.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-ink-secondary">Total</span>
                  <span className="font-medium tabular-nums text-ink">
                    ${(subtotal + platformFee).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-ink-secondary">Paid</span>
                  <span className="font-medium tabular-nums text-ink">
                    −${amountPaid.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between gap-3 border-t border-border pt-2">
                  <span className="text-sm font-bold text-ink">Balance due</span>
                  <span className="text-base font-bold tabular-nums text-ink sm:text-lg">
                    ${balanceAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {paymentTerms && balanceAmount > 0 && (
              <div className="mb-3 rounded-xl border border-border bg-surface-muted p-3 sm:mb-4 sm:p-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentSchedule(!showPaymentSchedule)}
                  className="flex w-full items-center justify-between gap-2 text-left"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Calendar className="h-4 w-4 shrink-0 text-ink" />
                    <h3 className="truncate text-sm font-bold text-ink sm:text-base">
                      Choose payment option
                    </h3>
                  </div>
                  {showPaymentSchedule ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-ink-secondary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-ink-secondary" />
                  )}
                </button>

                {showPaymentSchedule && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[11px] leading-relaxed text-ink-secondary sm:text-xs">
                      {showPayAllOption
                        ? 'Pay the full balance now, or the next installment on your plan.'
                        : isFinalInstallmentOnly && remainingPayments[0]
                          ? `Pay the remaining ${remainingPayments[0].label || 'balance'} to complete your booking.`
                          : 'Pay the full remaining balance to complete your booking.'}
                    </p>

                    {showPayAllOption ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPayAllRemaining(true);
                          setSelectedPayment(null);
                        }}
                        className={`w-full rounded-lg border-2 p-2.5 text-left transition-colors touch-manipulation sm:p-3 ${
                          payAllRemaining
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-border bg-surface hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <DollarSign
                                className={`h-3.5 w-3.5 shrink-0 ${
                                  payAllRemaining ? 'text-emerald-600' : 'text-gray-400'
                                }`}
                              />
                              <span className="text-xs font-semibold text-ink sm:text-sm">
                                Pay all remaining
                              </span>
                            </div>
                            <p className="mt-0.5 pl-5 text-[11px] text-ink-secondary sm:text-xs">
                              Full balance now
                              {(() => {
                                const totalDue = subtotal + platformFee;
                                const remainingPercentage =
                                  totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
                                const proportionalFee =
                                  (platformFee * remainingPercentage) / 100;
                                return proportionalFee > 0
                                  ? ` · includes $${proportionalFee.toFixed(2)} fee`
                                  : '';
                              })()}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs font-bold tabular-nums text-ink sm:text-sm">
                            ${balanceAmount.toFixed(2)}
                          </span>
                        </div>
                      </button>
                    ) : null}

                    {/* When final installment = balance, show one row (pay remaining). */}
                    {isFinalInstallmentOnly ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPayAllRemaining(true);
                          setSelectedPayment(null);
                        }}
                        className="w-full rounded-lg border-2 border-ink bg-surface p-2.5 text-left touch-manipulation sm:p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <DollarSign className="h-3.5 w-3.5 shrink-0 text-ink" />
                              <span className="text-xs font-semibold text-ink sm:text-sm">
                                {remainingPayments[0]?.label || 'Pay remaining balance'}
                              </span>
                              {remainingPayments[0]?.percentage != null ? (
                                <span className="text-[11px] text-ink-tertiary">
                                  ({remainingPayments[0].percentage}%)
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-0.5 break-words pl-5 text-[11px] text-ink-secondary sm:text-xs">
                              {remainingPayments[0]?.dueDate || 'Full balance now'}
                              {(() => {
                                const totalDue = subtotal + platformFee;
                                const remainingPercentage =
                                  totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
                                const proportionalFee =
                                  (platformFee * remainingPercentage) / 100;
                                return proportionalFee > 0
                                  ? ` · includes $${proportionalFee.toFixed(2)} fee`
                                  : '';
                              })()}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs font-bold tabular-nums text-ink sm:text-sm">
                            ${balanceAmount.toFixed(2)}
                          </span>
                        </div>
                      </button>
                    ) : (
                      remainingPayments.length > 0 && (
                        <div className="space-y-2">
                          {remainingPayments.map((payment) => {
                            const isSelected =
                              !payAllRemaining && selectedPayment?.index === payment.index;
                            const proportionalFee =
                              (platformFee * payment.percentage) / 100;

                            return (
                              <button
                                type="button"
                                key={payment.index}
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setPayAllRemaining(false);
                                }}
                                className={`w-full rounded-lg border-2 p-2.5 text-left transition-colors touch-manipulation sm:p-3 ${
                                  isSelected
                                    ? 'border-ink bg-surface'
                                    : 'border-border bg-surface hover:border-border-strong'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                      <DollarSign
                                        className={`h-3.5 w-3.5 shrink-0 ${
                                          isSelected ? 'text-ink' : 'text-gray-400'
                                        }`}
                                      />
                                      <span className="text-xs font-semibold text-ink sm:text-sm">
                                        {payment.label}
                                      </span>
                                      <span className="text-[11px] text-ink-tertiary">
                                        ({payment.percentage}%)
                                      </span>
                                    </div>
                                    <p className="mt-0.5 break-words pl-5 text-[11px] text-ink-secondary sm:text-xs">
                                      {payment.dueDate}
                                      {proportionalFee > 0
                                        ? ` · includes $${proportionalFee.toFixed(2)} fee`
                                        : ''}
                                    </p>
                                  </div>
                                  <span className="shrink-0 text-xs font-bold tabular-nums text-ink sm:text-sm">
                                    ${payment.remainingAmount.toFixed(2)}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

            <StripePaymentForm
              amount={selectedPaymentAmount}
              bookingId={booking.id}
              paymentType={paymentType}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              metadata={paymentMetadata}
              compact
            />
          </div>
        </>
      )}
    </ModalShell>
  );
};

export default BalancePaymentModal;
